import { css } from '@emotion/css';
import { Avatar, Box, Group, Stack, Text, Typography } from '@mantine/core';
import { openModal } from '@mantine/modals';
import type { CleanActivity } from 'api/models';
import logo from 'components/Logo/assets/logo.svg';
import dayjs from 'dayjs';
import { useTranslation } from 'locales';
import { capitalize } from 'remeda';
import ActivityDetail, {
  getTitleKeyForActivity,
} from 'shared/components/ActivityDetail/ActivityDetail';
import { EmailDeliveryStatusCompact } from 'shared/components/ActivityDetail/EmailDeliveryStatusCompact';
import { EmailActivityItem } from './EmailActivityItem';

type ActivityItemProps = {
  activity: CleanActivity;
};

export const ActivityItem = ({ activity }: ActivityItemProps) => {
  const { t } = useTranslation();

  // @ts-expect-error - Terribly typed
  if (activity.type === 'external_mail') return <EmailActivityItem email={activity} />;
  if (activity.type === 'debtor_reaction') return null;

  const {
    attributes: { description, author, date, comment, profile_picture },
  } = activity;

  const handleClickActivity = (activity: CleanActivity) => {
    openModal({
      size: 'auto',
      title: t(getTitleKeyForActivity(activity.attributes as any)),
      children: <ActivityDetail activity={activity.attributes as any} />,
    });
  };

  const profilePictureUrl = author === 'Recovr' ? logo : profile_picture;

  return (
    <Box className={styles.activity}>
      <Group
        onClick={() => handleClickActivity(activity as CleanActivity)}
        wrap="nowrap"
        align="start"
        px="sm"
        py="lg"
      >
        <Avatar src={profilePictureUrl} name={author} size={45} />
        <Stack gap={4}>
          <Group gap={4}>
            <Text fw={500} c="gray.10">
              {author}
            </Text>
            <Text c="dimmed" size="sm">
              •
            </Text>
            <Text c="dimmed" size="sm">
              {capitalize(dayjs(date).format('LLL'))}
            </Text>
          </Group>
          <Typography c="gray.10" classNames={{ root: styles.brandifyBold }}>
            <div dangerouslySetInnerHTML={{ __html: description }} />
          </Typography>
          {comment && (
            <Box id="comment-box" bg="purple.1" p={8} style={{ borderRadius: 8 }} mt="space2">
              <Text c="gray.10">{comment}</Text>
            </Box>
          )}
          {activity.attributes.email_statuses && (
            <Box mt="space2" ml={-2}>
              <EmailDeliveryStatusCompact statuses={activity.attributes.email_statuses} />
            </Box>
          )}
        </Stack>
      </Group>
    </Box>
  );
};

const styles = {
  brandifyBold: css`
    b {
      font-weight: 500;
      color: var(--mantine-color-purple-5);
    }
  `,
  activity: css`
    &:not(:last-child) {
      border-bottom: 1px solid var(--mantine-color-gray-3);
    }


    > div {
      transition: background-color 0.15s;
      cursor: pointer;
      background-color: transparent;
    }

    &:hover > div {
      background-color: var(--mantine-color-purple-1);
    }

    #commet-box {
      transition: background-color 0.15s;
    }

    &:hover #comment-box {
      background-color: var(--mantine-color-purple-2) !important;
    }
  `,
};
